import type { CreateApplicationInput, UpdateApplicationInput } from "@nudgebox/contracts";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api.ts";
import { queryKeys } from "@/lib/queries.ts";

export function useCreateApplication(org: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateApplicationInput) => api.applications.create({ org, input }),
    meta: { silentError: true }, // forms render errors inline
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.org(org) }),
  });
}

export function useUpdateApplication(org: string, app: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateApplicationInput) => api.applications.update({ org, app, input }),
    meta: { silentError: true },
    onSuccess: async (updated) => {
      if (updated.slug !== app) queryClient.removeQueries({ queryKey: queryKeys.app(org, app) });
      await queryClient.invalidateQueries({ queryKey: queryKeys.org(org) });
    },
  });
}

export function useDeleteApplication(org: string, app: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.applications.delete({ org, app }),
    onSuccess: async () => {
      queryClient.removeQueries({ queryKey: queryKeys.app(org, app) });
      await queryClient.invalidateQueries({ queryKey: queryKeys.org(org) });
    },
  });
}
